import { computed, Injectable, Signal, signal } from '@angular/core';

export interface OpenRequestTab {
    id: string;
    displayId: string;
}

/**
 * Holds the request "tabs" the user has opened while working through the task
 * lists. The tabs are shared between the task overview (where they are rendered
 * inline) and the sidebar (where they are listed below the Requests item) and
 * are persisted to {@link Window.sessionStorage} so they survive in-app
 * navigation and full page reloads while being discarded when the tab is closed.
 */
@Injectable({ providedIn: 'root' })
export class OpenRequestTabsService {
    private static readonly STORAGE_KEY = 'mdla:open-request-tabs';

    /** Maximum number of request tabs kept open at the same time. */
    public static readonly MAX_TABS = 1;

    private readonly _tabs = signal<OpenRequestTab[]>(this.restore());
    private readonly _activeId = signal<string | null>(null);

    public readonly tabs: Signal<OpenRequestTab[]> = this._tabs.asReadonly();
    public readonly activeId: Signal<string | null> = this._activeId.asReadonly();

    /** The currently open request tab, or `null` when no request tab is active. */
    public readonly activeTab = computed(() => this._tabs().find((tab) => tab.id === this._activeId()) ?? null);

    /**
     * Opens (or focuses) a tab for the given request and makes it the active one.
     * When {@link OpenRequestTabsService.MAX_TABS} is exceeded the oldest tab that
     * is neither active nor the newly opened one is closed.
     */
    public open(tab: OpenRequestTab): void {
        if (!this._tabs().some((existing) => existing.id === tab.id)) {
            this._tabs.update((tabs) => this.enforceLimit([...tabs, tab], tab.id));
            this.persist();
        }
        this._activeId.set(tab.id);
    }

    /** Marks the tab with the given id as active, or clears the active tab with `null`. */
    public setActive(id: string | null): void {
        this._activeId.set(id);
    }

    /** Updates the display id (used for the tab label) once it becomes known. */
    public updateDisplayId(id: string, displayId: string): void {
        if (!displayId) {
            return;
        }
        let changed = false;
        this._tabs.update((tabs) =>
            tabs.map((tab) => {
                if (tab.id === id && tab.displayId !== displayId) {
                    changed = true;
                    return { ...tab, displayId };
                }
                return tab;
            })
        );
        if (changed) {
            this.persist();
        }
    }

    /** Closes the tab for the given request id, clearing the active tab if it was active. */
    public close(id: string): void {
        this._tabs.update((tabs) => tabs.filter((tab) => tab.id !== id));
        if (this._activeId() === id) {
            this._activeId.set(null);
        }
        this.persist();
    }

    /** Drops the oldest tabs until the limit is met, keeping the active and the given tab. */
    private enforceLimit(tabs: OpenRequestTab[], keepId: string | null): OpenRequestTab[] {
        if (tabs.length <= OpenRequestTabsService.MAX_TABS) {
            return tabs;
        }
        const protectedIds = new Set([keepId, this._activeId()].filter((id): id is string => id !== null));
        const remaining = [...tabs];
        for (let index = 0; index < remaining.length && remaining.length > OpenRequestTabsService.MAX_TABS; ) {
            if (protectedIds.has(remaining[index].id)) {
                index++;
                continue;
            }
            remaining.splice(index, 1);
        }
        return remaining;
    }

    private persist(): void {
        const storage = this.getSessionStorage();
        if (!storage) {
            return;
        }
        try {
            storage.setItem(OpenRequestTabsService.STORAGE_KEY, JSON.stringify(this._tabs()));
        } catch {
            // Ignore storage write failures (e.g. quota or sandboxed iframes).
        }
    }

    private restore(): OpenRequestTab[] {
        const storage = this.getSessionStorage();
        if (!storage) {
            return [];
        }
        try {
            const saved = storage.getItem(OpenRequestTabsService.STORAGE_KEY);
            if (!saved) {
                return [];
            }
            const parsed: unknown = JSON.parse(saved);
            return Array.isArray(parsed)
                ? parsed
                      .filter(
                          (tab): tab is OpenRequestTab =>
                              typeof tab?.id === 'string' && typeof tab?.displayId === 'string'
                      )
                      .slice(-OpenRequestTabsService.MAX_TABS)
                : [];
        } catch {
            return [];
        }
    }

    private getSessionStorage(): Storage | null {
        try {
            if (typeof window === 'undefined' || !window.sessionStorage) {
                return null;
            }
            return window.sessionStorage;
        } catch {
            // Accessing sessionStorage can throw (e.g. sandboxed iframes or
            // privacy settings); treat it as unavailable.
            return null;
        }
    }
}
